import {forwardRef} from 'react';
import cx from 'classnames';

import {Select, Text, TextInput} from '#component/base';
import {sprinkles} from '#style/sprinkles';

import type {TextInputFieldProps} from './TextInputField.types';

import * as css from './TextInputField.css';

export const TextInputField = forwardRef(
  <N extends string = string, T extends React.ElementType = 'input'>(
    {
      className,
      disabled,
      errors,
      label,
      labelRightContent,
      id,
      name,
      options,
      message,
      type,
      onChange,
      ...otherProps
    }: TextInputFieldProps<N, T>,
    ref?: PolymorphicRef<T>,
  ) => {
    const renderedId = id || name;
    const hasError = !!errors?.[name];

    return (
      <div className={cx(css.root({disabled}), className)}>
        <label className={sprinkles({content: 'between', flex: 'flex'})} htmlFor={renderedId}>
          {label && (
            <Text color={hasError ? 'solidRed' : 'doveGray'} variant="label">
              {label}
            </Text>
          )}
          {labelRightContent && (
            <Text color="doveGray" variant="caption">
              {labelRightContent}
            </Text>
          )}
        </label>

        <div className={sprinkles({flex: 'flex', dir: 'col', gap: 8})}>
          {type === 'select' ? (
            <Select
              disabled={disabled}
              error={hasError}
              id={renderedId}
              label={label}
              name={name}
              onChange={onChange as React.ChangeEventHandler<HTMLSelectElement>}
              options={options}
              ref={ref}
              {...otherProps}
            />
          ) : (
            <TextInput
              disabled={disabled}
              error={hasError}
              id={renderedId}
              name={name}
              onChange={onChange}
              ref={ref}
              type={type}
              {...otherProps}
            />
          )}

          {message && message(renderedId)}

          {hasError && (
            <Text as="label" color="solidRed" htmlFor={renderedId}>
              {errors?.[name]?.message}
            </Text>
          )}
        </div>
      </div>
    );
  },
);
